import React, { useEffect, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { isMobileDevice } from '../../../utils/mobileUtils';

const ReplyModal = ({ 
  isOpen, 
  onClose, 
  children, 
  title = "Reply to Post" 
}) => {
  const modalRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  
  // Disable scroll lock to prevent navigation issues
  // useScrollLock({ isActive: isOpen });

  // Detect mobile device
  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Handle click outside
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };



  // Handle virtual keyboard on mobile
  useEffect(() => {
    if (!isMobile || !isOpen) return;

    const handleViewportChange = () => {
      const currentHeight = window.visualViewport ? 
        window.visualViewport.height : 
        window.innerHeight;
      
      const screenHeight = window.screen.height;
      const keyboardThreshold = screenHeight * 0.75;
      const isKeyboardVisible = currentHeight < keyboardThreshold;
      
      setKeyboardVisible(isKeyboardVisible);
    };

    // Use visualViewport API if available
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
    } else {
      window.addEventListener('resize', handleViewportChange);
    }
    
    window.addEventListener('orientationchange', handleViewportChange);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
      } else {
        window.removeEventListener('resize', handleViewportChange);
      }
      window.removeEventListener('orientationchange', handleViewportChange);
    };
  }, [isMobile, isOpen]);

  // Focus management
  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div 
      className={`modal-backdrop fixed inset-0 z-50 flex ${
        isMobile ? 'items-stretch' : 'items-start justify-center'
      } ${isMobile ? 'p-0' : 'p-4'} bg-black/50 backdrop-blur-sm`}
      onClick={handleBackdropClick}
    >
      <div 
        ref={modalRef}
        className={`modal-container bg-white dark:bg-gray-800 shadow-2xl flex flex-col ${
          isMobile 
            ? 'w-full h-full rounded-none' 
            : 'rounded-xl w-full max-w-3xl max-h-[95vh] mt-4 sm:mt-8'
        } ${keyboardVisible && isMobile ? 'pb-4' : ''}`}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Modal Header - simplified */}
        <div className="modal-header flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 id="modal-title" className="text-base font-medium text-gray-900 dark:text-white">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="modal-scroll-container flex-1 overflow-y-auto">
          <div className="modal-content p-4 min-h-[400px]">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReplyModal;